import { ProjectionCategoryEnum } from '@enums/projection-category.enum';
import { TaxRegimeCode } from '@enums/tax-regime-code.enum';
import { ProjectionLineGroupedByCategory } from '@interfaces/projection-line-grouped-by-category';
import { ProjectionSubCategory } from '@interfaces/projection-sub-category';
import { ProjectionIndicatorLine, ProjectionIndicatorLineJson } from '@models/projection-indicator-line';
import { ProjectionLine, ProjectionLineJson } from '@models/projection-line';

export interface ProjectionJson {
  id?: number;
  start_date?: string;
  end_date?: string;
  accounting_period_end_date?: string;
  coefficient?: number;
  projection_lines?: ProjectionLineJson[];
  tax_regime?: TaxRegimeCode;
  projection_indicators?: ProjectionIndicatorLineJson[];
  comment?: string | null;
}

export class Projection {
  constructor(
    public id?: number,
    public startDate?: string,
    public endDate?: string,
    public accountingPeriodEndDate?: string,
    public coefficient?: number,
    public projectionLines?: ProjectionLine[],
    public taxRegime?: TaxRegimeCode,
    public projectionIndicators?: ProjectionIndicatorLine[],
    public comment?: string | null
  ) {}

  static fromJson(json: ProjectionJson): Projection {
    return new Projection(
      json.id,
      json.start_date,
      json.end_date,
      json.accounting_period_end_date,
      json.coefficient,
      json.projection_lines
        ? json.projection_lines.map((projectionLine: ProjectionLineJson) => ProjectionLine.fromJson(projectionLine))
        : null,
      json.tax_regime,
      json.projection_indicators
        ? json.projection_indicators.map((projectionIndicator: ProjectionIndicatorLineJson) =>
            ProjectionIndicatorLine.fromJson(projectionIndicator)
          )
        : null,
      json.comment
    );
  }

  static toJson(model: Projection): ProjectionJson {
    return {
      id: model.id,
      start_date: model.startDate,
      end_date: model.endDate,
      accounting_period_end_date: model.accountingPeriodEndDate,
      coefficient: model.coefficient,
      projection_lines: model.projectionLines ? Projection.toJsonProjectionLines(model) : undefined,
      projection_indicators: model.projectionIndicators
        ? model.projectionIndicators.map(ProjectionIndicatorLine.toJson)
        : undefined,
      comment: model.comment
    };
  }

  static toJsonProjectionLines(model: Projection): ProjectionLineJson[] {
    return (model.projectionLines ?? [])
      .sort((a: ProjectionLine, b: ProjectionLine) => (a.id < b.id ? -1 : 1))
      .map((projectionLine: ProjectionLine) => ProjectionLine.toJson(projectionLine));
  }

  static groupProjectionLinesByCategory(projection: Projection): ProjectionLineGroupedByCategory[] {
    return projection.projectionLines.reduce((acc: ProjectionLineGroupedByCategory[], currentValue: ProjectionLine) => {
      const projectionCategory: ProjectionLineGroupedByCategory = acc.find(
        (value: ProjectionLineGroupedByCategory) => value.category === currentValue.projectionCategory
      );

      if (projectionCategory) {
        const projectionSubCategory: ProjectionSubCategory = projectionCategory.subCategories.find(
          value => value.subCategory === currentValue.projectionLineCategory
        );

        if (projectionSubCategory) {
          projectionSubCategory.projectionLines.push(currentValue);
        } else {
          projectionCategory.subCategories.push({
            subCategory: currentValue.projectionLineCategory,
            type: currentValue.type,
            projectionLines: [currentValue]
          });
        }
      } else {
        acc.push({
          category: currentValue.projectionCategory,
          subCategories: [
            {
              subCategory: currentValue.projectionLineCategory,
              type: currentValue.type,
              projectionLines: [currentValue]
            }
          ]
        });
      }

      return acc;
    }, [] as ProjectionLineGroupedByCategory[]);
  }

  static addingNonExistingProjectionLineCategories(
    groups: ProjectionLineGroupedByCategory[]
  ): ProjectionLineGroupedByCategory[] {
    Object.entries(ProjectionCategoryEnum).forEach(([_, projectionCategoryEnum]: [string, ProjectionCategoryEnum]) => {
      const findedProjectionLineGroupedByCategory: ProjectionLineGroupedByCategory = groups.find(
        (projectionLineGroupedByCategory: ProjectionLineGroupedByCategory) =>
          projectionLineGroupedByCategory.category === projectionCategoryEnum
      );
      if (!findedProjectionLineGroupedByCategory) {
        groups.push({ category: projectionCategoryEnum, subCategories: [] });
      }
    });
    return groups;
  }

  static sortCategories(groups: ProjectionLineGroupedByCategory[]): ProjectionLineGroupedByCategory[] {
    return [...groups].sort((a, b) => Projection.getCategoryRank(a.category) - Projection.getCategoryRank(b.category));
  }

  private static getCategoryRank(category: ProjectionCategoryEnum): number {
    switch (category) {
      case ProjectionCategoryEnum.salesAmount:
        return 1;
      case ProjectionCategoryEnum.brutMargin:
        return 2;
      case ProjectionCategoryEnum.addedValue:
        return 3;
      case ProjectionCategoryEnum.brutExploitationProfit:
        return 4;
      case ProjectionCategoryEnum.currentResult:
        return 5;
      case ProjectionCategoryEnum.netResult:
        return 6;
      default:
        return -1;
    }
  }
}
